import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Edit, Trash2, Star, ToggleLeft, ToggleRight, Search } from "lucide-react";
import { supabase } from "../../lib/supabase";
import { Spinner, EmptyState, ConfirmModal } from "../../components/common/UI";
import toast from "react-hot-toast";

export default function AdminRestaurants() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from("restaurants")
      .select("*, users(name, email)")
      .order("created_at", { ascending: false });
    if (search) query = query.ilike("name", `%${search}%`);
    const { data } = await query;
    setRestaurants(data || []);
    setLoading(false);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetch, 300);
    return () => clearTimeout(t);
  }, [fetch]);

  const toggleActive = async (id, current) => {
    await supabase.from("restaurants").update({ is_active: !current }).eq("id", id);
    setRestaurants((p) => p.map((r) => r.id === id ? { ...r, is_active: !current } : r));
    toast.success(current ? "Restaurant deactivated" : "Restaurant activated");
  };

  const toggleFeatured = async (id, current) => {
    await supabase.from("restaurants").update({ is_featured: !current }).eq("id", id);
    setRestaurants((p) => p.map((r) => r.id === id ? { ...r, is_featured: !current } : r));
    toast.success(current ? "Removed from featured" : "Added to featured");
  };

  const handleDelete = async () => {
    const { error } = await supabase.from("restaurants").delete().eq("id", deleteTarget.id);
    if (error) { toast.error(error.message); return; }
    setRestaurants((p) => p.filter((r) => r.id !== deleteTarget.id));
    toast.success("Restaurant deleted");
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-6 page-enter">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900">Restaurants</h1>
          <p className="text-gray-500 text-sm">{restaurants.length} restaurant(s)</p>
        </div>
        <Link to="/admin/restaurants/new" className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Restaurant
        </Link>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="    Search restaurants..."
          className="input-field pl-10 py-2.5"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : restaurants.length === 0 ? (
        <EmptyState
          icon="🍽️"
          title="No restaurants yet"
          description="Add your first restaurant to get started."
          action={<Link to="/admin/restaurants/new" className="btn-primary">Add Restaurant</Link>}
        />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100 bg-gray-50">
                  <th className="px-5 py-3 text-left">Restaurant</th>
                  <th className="px-5 py-3 text-left">City</th>
                  <th className="px-5 py-3 text-left">Owner</th>
                  <th className="px-5 py-3 text-left">Rating</th>
                  <th className="px-5 py-3 text-left">Status</th>
                  <th className="px-5 py-3 text-left">Featured</th>
                  <th className="px-5 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {restaurants.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        {r.logo_url ? (
                          <img src={r.logo_url} alt={r.name} className="w-9 h-9 rounded-xl object-cover" />
                        ) : (
                          <div className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center text-lg">🍽️</div>
                        )}
                        <div>
                          <p className="font-semibold text-gray-900">{r.name}</p>
                          <p className="text-xs text-gray-400">{r.cuisine_type?.join(", ")}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-gray-600">{r.city}</td>
                    <td className="px-5 py-4">
                      <p className="text-gray-700">{r.users?.name || "—"}</p>
                      <p className="text-xs text-gray-400">{r.users?.email}</p>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1">
                        <span className="star text-sm">★</span>
                        <span className="font-medium">{r.rating > 0 ? r.rating.toFixed(1) : "—"}</span>
                        <span className="text-gray-400 text-xs">({r.review_count})</span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => toggleActive(r.id, r.is_active)} className="flex items-center gap-1.5 text-xs font-medium">
                        {r.is_active ? (
                          <><ToggleRight className="w-5 h-5 text-green-500" /><span className="text-green-600">Active</span></>
                        ) : (
                          <><ToggleLeft className="w-5 h-5 text-gray-400" /><span className="text-gray-400">Inactive</span></>
                        )}
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => toggleFeatured(r.id, r.is_featured)} className="flex items-center gap-1 text-xs font-medium">
                        <Star className={`w-4 h-4 ${r.is_featured ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`} />
                        <span className={r.is_featured ? "text-yellow-600" : "text-gray-400"}>
                          {r.is_featured ? "Yes" : "No"}
                        </span>
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/admin/restaurants/${r.id}/edit`}
                          className="p-1.5 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => setDeleteTarget(r)}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Delete Restaurant"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This will also delete all menu items and cannot be undone.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        danger
      />
    </div>
  );
}
